import { useCallback, useMemo } from 'react';

import { get, isNil, toUpper } from 'lodash';
import { useSelector } from 'react-redux';

import type { EventsCommunicationType } from '@appTypes/esb';
import {
  esbApi,
  useDeleteEventsCommunicationsMutation,
  useGetEventsCommunicationsOrganizationsQuery,
  usePostEventsCommunicationsMutation,
} from '@modules/rtk/esb-api';
import { toastService } from '@modules/toasts';
import {
  newOrgPermissionsSel,
  organizationGuidSel,
  personGuidSel,
  userDataSel,
} from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import { newPermissions } from '@shared/newPermissions';

import { getQueryParams, toSafeHtml } from './hooks.utils';

const NATIONAL_ORG_GUID = 'F3A77C78-EE20-474E-9219-D53400753B29';
const MAX_VISIBLE_BANNERS = 2;

export function useEventsCommunications() {
  const personGuid = useSelector(personGuidSel);
  const organizationGuid =
    useSelector(organizationGuidSel) || NATIONAL_ORG_GUID;
  const roleTypeQueryState =
    personGuid &&
    esbApi.endpoints.getRoleTypes.useQueryState({
      personGuid,
      params: {
        includeParentRoles: 'true',
        includeScoutbookRoles: 'true',
      },
    });

  const permissionsObjectByGuid = useSelector(newOrgPermissionsSel);
  const userData = useSelector(userDataSel);
  const isNationalAdmin = get(roleTypeQueryState, 'data', []).some(
    ({ organizationGuid, role }) =>
      toUpper(organizationGuid) === toUpper(NATIONAL_ORG_GUID) &&
      toUpper(role) === toUpper('National Admin'),
  );

  const queryParams = useMemo(
    () => getQueryParams(organizationGuid),
    [organizationGuid],
  );

  const [postEventsCommunications, { isLoading: isPosting }] =
    usePostEventsCommunicationsMutation();
  const [deleteEventsCommunications, { isLoading: isDeleting }] =
    useDeleteEventsCommunicationsMutation();
  const { isLoading, data } =
    useGetEventsCommunicationsOrganizationsQuery(queryParams);

  // Only one banner is allowed per organization; it is shown first
  const { announcements, orgAnnouncement } = useMemo(() => {
    const events = data?.events || [];
    const orgEvent = events.find(
      event => toUpper(event.organizationGuid) === toUpper(organizationGuid),
    );
    const visibleEvents = (
      orgEvent
        ? [orgEvent, ...events.filter(event => event !== orgEvent)]
        : events
    ).slice(0, MAX_VISIBLE_BANNERS);

    const mapped = visibleEvents.map(
      ({ announcementMessage, createdByPersonGuid, ...rest }) => ({
        ...rest,
        announcementMessage: toSafeHtml(announcementMessage),
        own:
          !!personGuid &&
          !!createdByPersonGuid &&
          toUpper(personGuid) === toUpper(createdByPersonGuid),
      }),
    );

    return {
      announcements: mapped,
      orgAnnouncement: orgEvent ? mapped[0] : undefined,
    };
  }, [data, organizationGuid, personGuid]);

  const postEvent = useCallback(
    async (
      announcementTitle: string,
      announcementMessage: string,
      startDateTime: string,
      endDateTime: string,
    ) => {
      const body = {
        announcementMessage,
        announcementTitle,
        communicationType: 'Announcement' as EventsCommunicationType,
        createdByMemberId: userData?.profile?.memberId,
        createdByPersonGuid: personGuid as string,
        endDateTime,
        startDateTime,
        targetOrganizations: [
          {
            everyChildOrganization: 'true',
            organizationGuid: organizationGuid,
          },
        ],
      };

      try {
        await postEventsCommunications(body).unwrap();

        toastService.success(
          intl.formatMessage({ id: 'core.banner.addedSuccess' }),
        );
      } catch (error) {
        toastService.error(
          intl.formatMessage({ id: 'core.banner.addedError' }),
        );

        throw error;
      }
    },
    [organizationGuid, personGuid, postEventsCommunications, userData],
  );

  const deleteEvent = useCallback(
    async (eventGuid: string) => {
      try {
        await deleteEventsCommunications({ eventGuid }).unwrap();

        toastService.success(
          intl.formatMessage({ id: 'core.banner.removeSuccess' }),
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        toastService.error(
          intl.formatMessage({ id: 'core.banner.removeError' }),
        );
      }
    },
    [deleteEventsCommunications],
  );

  const isEditAllowed = useCallback(
    (orgGuid: string | null = NATIONAL_ORG_GUID) => {
      if (isNil(orgGuid) || toUpper(orgGuid) === toUpper(NATIONAL_ORG_GUID)) {
        return isNationalAdmin;
      }

      const upperOrgGuid = toUpper(orgGuid);

      if (upperOrgGuid !== toUpper(organizationGuid)) {
        return false;
      }

      const orgPermissions = permissionsObjectByGuid[upperOrgGuid];

      return [newPermissions.EDIT_BANNER].some(
        permission => !!orgPermissions?.[permission],
      );
    },
    [isNationalAdmin, organizationGuid, permissionsObjectByGuid],
  );

  return {
    announcements,
    deleteEvent,
    isEditAllowed,
    loading: isPosting || isDeleting || isLoading,
    orgAnnouncement,
    postEvent,
  };
}
